import React from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';

const STATUS_TRANSITIONS = {
    pending:   { next: 'preparing', label: 'Iniciar preparo' },
    preparing: { next: 'ready',     label: 'Marcar como pronto' },
    ready:     { next: 'delivered', label: 'Confirmar entrega' },
    delivered: { next: null,        label: null },
};

export default function OrderCard({ order, onAdvance, color, isAdvancing = false }) {
    const elapsed = Math.floor((Date.now() - new Date(order.created_at).getTime()) / 60000);
    const elapsedLabel = elapsed < 60
        ? `${elapsed}min`
        : `${Math.floor(elapsed / 60)}h ${elapsed % 60}min`;
    const transition = STATUS_TRANSITIONS[order.status] || STATUS_TRANSITIONS.pending;
    const isUrgent = elapsed > 15;

    return (
        <Card $color={color}>
            <CardHeader>
                <OrderNumber>#{order.id}</OrderNumber>
                <TabPill>{order.table_identifier || order.person_name || '—'}</TabPill>
                <ElapsedTime $urgent={isUrgent}>
                    há {elapsedLabel}
                </ElapsedTime>
            </CardHeader>
            {order.created_by?.name && (
                <WaiterLabel>🧑‍🍳 Garçom: {order.created_by.name}</WaiterLabel>
            )}

            {order.observation && (
                <OrderObservation>{order.observation}</OrderObservation>
            )}

            <ItemsList>
                {order.items?.map((item, i) => (
                    <OrderItem key={i}>
                        <ItemQty>{item.quantity}×</ItemQty>
                        <ItemDetails>
                            <ItemName>{item.product_name}</ItemName>
                            {item.options?.length > 0 && (
                                <ItemOptions>
                                    {item.options.map(opt => opt.name).join(', ')}
                                </ItemOptions>
                            )}
                            {item.observation && (
                                <ItemObs>{item.observation}</ItemObs>
                            )}
                        </ItemDetails>
                    </OrderItem>
                ))}
            </ItemsList>

            {transition.label && (
                <AdvanceBtn $color={color} disabled={isAdvancing} onClick={() => onAdvance(order.id, transition.next)}>
                    {transition.label}
                </AdvanceBtn>
            )}
        </Card>
    );
}

/* Styled Components */

const Card = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 14px;
    border-left: 4px solid ${({ $color }) => $color || colors.neutralDark};
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const OrderNumber = styled.span`
    font-weight: 700;
    font-size: 16px;
    color: ${colors.nightDark};
`;

const TabPill = styled.span`
    flex: 1;
    font-size: 12px;
    color: ${colors.night};
    background: ${colors.neutralDark};
    padding: 3px 10px;
    border-radius: 10px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
`;

const ElapsedTime = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${({ $urgent }) => $urgent ? colors.danger : colors.nightLight};
`;

const WaiterLabel = styled.div`
    font-size: 12px;
    color: ${colors.nightLight};
    margin-bottom: 10px;
`;

const OrderObservation = styled.div`
    font-size: 13px;
    color: ${colors.danger};
    background: ${colors.danger}10;
    border: 1px dashed ${colors.danger}40;
    padding: 6px 10px;
    border-radius: 6px;
    margin-bottom: 10px;
    font-weight: 500;
`;

const ItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
`;

const OrderItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const ItemQty = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
    min-width: 24px;
    margin-top: 1px;
`;

const ItemDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const ItemName = styled.span`
    font-size: 14px;
    color: ${colors.nightDark};
`;

const ItemOptions = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    font-style: italic;
`;

const ItemObs = styled.span`
    font-size: 12px;
    color: ${colors.danger};
    font-weight: 500;
`;

const AdvanceBtn = styled.button`
    width: 100%;
    padding: 11px;
    border-radius: 8px;
    background: ${({ $color }) => $color || colors.occasionalPurple};
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
